import React, { PropsWithChildren, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';

import { PERMISSIONS } from 'api/auth/constants';
import { PERMISSION_TYPE, useRBAC } from 'hooks/useRBAC';
import { useStatePath } from 'hooks/useStatePath';
import { useAuthentication, useSessionRecover } from 'queries/session';
import { useIdleLogout } from 'queries/session/useIdleLogout';
import { IRoutesConfigItem, ROUTES_CONFIG } from 'routes/config';
import { ROUTES } from 'routes/config/constants';
import { ReloginRedirect } from 'utils/reloginRedirect';

import { AppContainer } from './styled';

const Maintenance = React.lazy(
  () => import('routes/pages/UnauthorizedStack/SomethingWrongPages/Maintenance'),
);

type TRouteWithAuthProps = PropsWithChildren<{
  isPrivate: boolean;
  permissions?: PERMISSIONS[];
  permissionType?: PERMISSION_TYPE;
}>;

const RouteWithAuth = ({
  children,
  isPrivate,
  permissions,
  permissionType,
}: TRouteWithAuthProps): null | React.ReactElement => {
  const { enabled } = useRBAC({ conditionType: permissionType, list: permissions });
  const location = useLocation();

  const { isAuthorized } = useAuthentication();

  if (isAuthorized && location.pathname === ROUTES.SIGN_IN.PATH) {
    return <Navigate replace to={ROUTES.HOME.PATH} />;
  }

  if (isPrivate && !isAuthorized && location.pathname !== ROUTES.SIGN_IN.PATH) {
    return <Navigate state={{ from: location }} to={ROUTES.SIGN_IN.PATH} />;
  }

  if (permissions && !enabled) {
    return <Navigate replace to={ROUTES.PERMISSION_DENIED.PATH} />;
  }

  return <>{children}</>;
};

const getComponent = (component: React.ComponentClass | React.FC): React.ReactElement => {
  if (React.isValidElement(component)) {
    return component;
  }

  const Component = component;
  return <Component />;
};

const getRoute = (
  {
    children,
    component,
    isPrivate = false,
    path,
    permissions,
    permissionType,
  }: IRoutesConfigItem,
  key: number,
) => {
  return (
    <Route
      element={
        <RouteWithAuth
          isPrivate={isPrivate}
          permissions={permissions}
          permissionType={permissionType}
        >
          {getComponent(component)}
        </RouteWithAuth>
      }
      key={key}
      path={path}
    >
      {!!children && children.map(getRoute)}
    </Route>
  );
};

const IndexRoute: React.FC = () => {
  useSessionRecover();
  useIdleLogout();
  const { isInitialized, isThemeRequested } = useAuthentication();
  const statePath = useStatePath();

  useEffect(() => {
    ReloginRedirect.setSessionPath(statePath);
  }, []);

  if (!isInitialized || !isThemeRequested) {
    return null;
  }

  const renderApp = () => {
    if (global.CONFIG.MAINTENANCE_MODE) {
      return (
        <Routes>
          <Route element={<Maintenance />} path='*' />
        </Routes>
      );
    }

    return <Routes>{ROUTES_CONFIG.map(getRoute)}</Routes>;
  };

  return <AppContainer>{renderApp()}</AppContainer>;
};

export default IndexRoute;
